import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Activity as ActivityIcon, Heart, MessageCircle, Share2, Rocket, Trophy, Star, Sparkles } from "lucide-react";
import api from "@/lib/api";
import { Spinner, EmptyState } from "@/components/Bits";
import { toast } from "sonner";

const timeAgo = (iso) => {
  if (!iso) return "";
  const s = Math.floor((Date.now() - new Date(iso).getTime()) / 1000);
  if (s < 60) return "just now";
  if (s < 3600) return `${Math.floor(s / 60)}m ago`;
  if (s < 86400) return `${Math.floor(s / 3600)}h ago`;
  return `${Math.floor(s / 86400)}d ago`;
};

const VERB = {
  published: { icon: Rocket, text: "published a new game", color: "text-emerald-500" },
  favorited: { icon: Star, text: "favourited a game", color: "text-amber-500" },
  achievement: { icon: Trophy, text: "earned an achievement", color: "text-violet-500" },
  wrapped: { icon: Sparkles, text: "shared their Wrapped", color: "text-fuchsia-500" },
};

export default function Activity() {
  const navigate = useNavigate();
  const [feed, setFeed] = useState(null);
  const [liked, setLiked] = useState({});

  useEffect(() => { api.get("/social/feed").then((r) => setFeed(r.data.feed || [])).catch(() => setFeed([])); }, []);

  const like = async (gameId, key) => {
    if (!gameId) return;
    setLiked((p) => ({ ...p, [key]: !p[key] }));
    try { await api.post(`/arcade/${gameId}/like`); } catch { setLiked((p) => ({ ...p, [key]: !p[key] })); }
  };
  const share = async (item) => {
    const url = item.game ? `${window.location.origin}/app/arcade/${item.game.id}` : `${window.location.origin}/app/creators/${item.actor.id}`;
    try { await navigator.clipboard.writeText(url); toast.success("Link copied! 🔗"); } catch { toast(url); }
  };
  const react = async (item, idx) => {
    if (!item.post_id) return;
    setFeed((f) => f.map((it, i) => i === idx
      ? { ...it, reacted: !it.reacted, reaction_count: (it.reaction_count || 0) + (it.reacted ? -1 : 1) } : it));
    try { await api.post(`/social/feed/${item.post_id}/react`); }
    catch { setFeed((f) => f.map((it, i) => i === idx ? { ...it, reacted: !it.reacted, reaction_count: (it.reaction_count || 0) + (it.reacted ? 1 : -1) } : it)); }
  };

  return (
    <div className="p-5 lg:p-8 max-w-2xl mx-auto">
      <div className="flex items-center gap-3 mb-2">
        <ActivityIcon className="w-8 h-8 text-[hsl(var(--primary))]" />
        <h1 className="font-display text-3xl font-extrabold">Activity Feed</h1>
      </div>
      <p className="text-[hsl(var(--muted-foreground))] mb-6">What your friends and the creators you follow are up to.</p>

      {!feed ? <Spinner /> : feed.length === 0 ? (
        <EmptyState emoji="📡" title="Your feed is quiet" text="Follow creators and add friends to see their latest games and wins here." />
      ) : (
        <div className="space-y-4" data-testid="activity-feed">
          {feed.map((item, i) => {
            const v = VERB[item.type] || VERB.published;
            const Icon = v.icon;
            const key = `${item.type}-${i}`;
            const a = item.actor || {};
            return (
              <div key={key} data-testid={`activity-item-${i}`} className="rounded-2xl border border-[hsl(var(--border))] bg-[hsl(var(--card))] p-4">
                <div className="flex items-center gap-3 mb-3">
                  <button onClick={() => navigate(`/app/creators/${a.id}`)} className="w-10 h-10 rounded-full grid place-items-center text-lg bg-gradient-to-br from-[#1f6feb] to-[#3b9eff] shrink-0">{a.avatar_emoji || "🎮"}</button>
                  <div className="min-w-0 flex-1">
                    <div className="text-sm"><button onClick={() => navigate(`/app/creators/${a.id}`)} className="font-bold hover:underline">{a.name}</button> <span className={`${v.color} font-medium`}>{v.text}</span></div>
                    <div className="text-[11px] text-[hsl(var(--muted-foreground))]">{timeAgo(item.ts)}</div>
                  </div>
                  <Icon className={`w-5 h-5 ${v.color} shrink-0`} />
                </div>

                {item.game && (
                  <button onClick={() => navigate(`/app/arcade/${item.game.id}`)} data-testid={`activity-game-${i}`} className="w-full flex items-center gap-3 rounded-xl bg-[hsl(var(--secondary))] p-3 hover:bg-[hsl(var(--border))] transition-colors text-left">
                    {item.game.cover_image ? <img src={`${process.env.REACT_APP_BACKEND_URL}${item.game.cover_image}`} alt="" className="w-12 h-12 rounded-lg object-cover" /> : <span className="text-3xl">{item.game.cover_emoji || "🎮"}</span>}
                    <span className="font-bold truncate">{item.game.title}</span>
                  </button>
                )}
                {item.achievement && (
                  <div className="flex items-center gap-3 rounded-xl bg-[hsl(var(--secondary))] p-3">
                    <span className="text-3xl">{item.achievement.icon}</span>
                    <span className="font-bold">{item.achievement.title}</span>
                  </div>
                )}
                {item.wrapped && (
                  <div className="rounded-xl p-4 text-white relative overflow-hidden" data-testid={`activity-wrapped-${i}`}
                    style={{ background: "linear-gradient(150deg, #150a2e 0%, #2a1259 45%, #10233f 100%)" }}>
                    <div className="text-[11px] font-bold uppercase tracking-wider text-white/70 flex items-center gap-1">🎮 GameView Wrapped · {item.wrapped.period_label || item.wrapped.month}</div>
                    <div className="grid grid-cols-2 sm:grid-cols-4 gap-2 mt-3">
                      {[["▶️", (item.wrapped.plays || 0).toLocaleString(), "plays"],
                        ["🫂", (item.wrapped.new_followers || 0).toLocaleString(), "new fans"],
                        ["💰", `$${(item.wrapped.earnings || 0).toFixed(0)}`, "earned"],
                        ["💜", `$${(item.wrapped.tips || 0).toFixed(0)}`, "tips"]].map(([e, v, l]) => (
                        <div key={l} className="rounded-lg bg-white/10 backdrop-blur p-2 text-center">
                          <div className="text-base">{e}</div>
                          <div className="font-display font-extrabold tabular-nums">{v}</div>
                          <div className="text-[10px] text-white/60">{l}</div>
                        </div>
                      ))}
                    </div>
                    {item.wrapped.top_game && <div className="mt-2 text-xs text-white/70">Top game: {item.wrapped.top_game.cover_emoji} <b>{item.wrapped.top_game.title}</b></div>}
                  </div>
                )}

                <div className="flex items-center gap-1 mt-3 text-sm text-[hsl(var(--muted-foreground))]">
                  {item.game && (
                    <>
                      <button onClick={() => like(item.game.id, key)} data-testid={`activity-like-${i}`} className="flex items-center gap-1.5 px-3 py-1.5 rounded-full hover:bg-[hsl(var(--secondary))] transition-colors">
                        <Heart className={`w-4 h-4 ${liked[key] ? "fill-rose-500 text-rose-500" : ""}`} /> Like
                      </button>
                      <button onClick={() => navigate(`/app/community/${item.game.id}`)} data-testid={`activity-comment-${i}`} className="flex items-center gap-1.5 px-3 py-1.5 rounded-full hover:bg-[hsl(var(--secondary))] transition-colors">
                        <MessageCircle className="w-4 h-4" /> Comment
                      </button>
                    </>
                  )}
                  {item.post_id && (
                    <button onClick={() => react(item, i)} data-testid={`activity-react-${i}`}
                      className={`flex items-center gap-1.5 px-3 py-1.5 rounded-full transition-colors ${item.reacted ? "bg-fuchsia-500/15 text-fuchsia-500 font-semibold" : "hover:bg-[hsl(var(--secondary))]"}`}>
                      <span className="text-base leading-none">🎉</span> Celebrate{item.reaction_count > 0 ? ` · ${item.reaction_count}` : ""}
                    </button>
                  )}
                  <button onClick={() => share(item)} data-testid={`activity-share-${i}`} className="flex items-center gap-1.5 px-3 py-1.5 rounded-full hover:bg-[hsl(var(--secondary))] transition-colors">
                    <Share2 className="w-4 h-4" /> Share
                  </button>
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}
